import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import dayjs from 'dayjs';
import { ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { merchantPayments } from '@/api/client';
import { Empty, ErrorBox } from '@/components/empty';
import { Json } from '@/components/json-view';
import { PaymentStatusBadge } from '@/components/status-badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { formatLyd } from '@/lib/utils';

export const Route = createFileRoute('/m/payments/$id')({
  component: MerchantPaymentDetail,
});

function MerchantPaymentDetail() {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['merchant-payment', id],
    queryFn: () => merchantPayments.get(id),
  });

  if (isError) return <ErrorBox message={t('payments.detail.not_found')} />;
  if (isLoading || !data) return <Empty title={t('common.loading')} />;

  return (
    <div className="space-y-6">
      <div>
        <Button asChild variant="ghost" size="sm">
          <Link to="/m/payments">
            <ArrowLeft className="h-4 w-4" /> {t('payments.detail.back_to_list')}
          </Link>
        </Button>
      </div>

      <header className="flex flex-wrap items-baseline justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{data.merchant_order_ref}</h1>
          <p className="text-sm text-muted-foreground font-mono">{data.id}</p>
        </div>
        <PaymentStatusBadge status={data.status} />
      </header>

      <section className="grid gap-3 grid-cols-2 md:grid-cols-4">
        <Field label={t('payments.cols.psp')} value={data.psp_code} />
        <Field label={t('payments.cols.amount')} value={formatLyd(data.amount_minor)} />
        <Field label={t('payments.cols.psp_ref')} value={data.psp_ref ?? '—'} mono />
        <Field label={t('payments.cols.currency')} value={data.currency} />
      </section>

      <Tabs defaultValue="timeline">
        <TabsList>
          <TabsTrigger value="timeline">{t('payments.detail.timeline')}</TabsTrigger>
          <TabsTrigger value="metadata">{t('payments.detail.metadata')}</TabsTrigger>
        </TabsList>

        <TabsContent value="timeline">
          <Card>
            <CardHeader>
              <CardTitle>{t('payments.detail.events')}</CardTitle>
            </CardHeader>
            <CardContent>
              <ol className="relative border-s border-border ps-5 space-y-4">
                {data.events.map((e) => (
                  <li key={e.id}>
                    <span className="absolute -start-1.5 mt-1.5 h-3 w-3 rounded-full bg-primary" />
                    <div className="text-xs text-muted-foreground font-mono">
                      {dayjs(e.created_at).format('YYYY-MM-DD HH:mm:ss')} · {e.source}
                    </div>
                    <div className="text-sm font-medium">
                      {e.event_type}
                      {e.new_status && (
                        <span className="text-muted-foreground"> → {e.new_status}</span>
                      )}
                    </div>
                  </li>
                ))}
              </ol>
              {data.events.length === 0 && <Empty title={t('payments.detail.no_events')} />}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="metadata">
          <Card>
            <CardHeader>
              <CardTitle>{t('payments.detail.metadata')}</CardTitle>
            </CardHeader>
            <CardContent>
              <Json data={data.metadata} />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Field({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="rounded-lg border border-border p-3">
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={mono ? 'mt-1 text-sm font-mono' : 'mt-1 text-sm font-medium capitalize'}>
        {value}
      </div>
    </div>
  );
}
